// Mappa Leaflet con i marker delle sedi di una company
// Richiede react-leaflet e leaflet (già installati)
import { useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix icona default Leaflet con Vite/Webpack
delete (L.Icon.Default.prototype as Record<string, unknown>)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

export interface SiteMapMarker {
  id: string
  name: string
  address?: string
  city?: string
  latitude: number
  longitude: number
}

interface FitBoundsProps {
  markers: SiteMapMarker[]
}

// Componente interno che adatta il viewport ai marker
function FitBounds({ markers }: FitBoundsProps) {
  const map = useMap()

  useEffect(() => {
    if (markers.length === 0) return
    if (markers.length === 1) {
      map.setView([markers[0].latitude, markers[0].longitude], 14)
      return
    }
    const bounds = L.latLngBounds(markers.map((m) => [m.latitude, m.longitude]))
    map.fitBounds(bounds, { padding: [40, 40] })
  }, [map, markers])

  return null
}

interface SiteMapProps {
  sites: SiteMapMarker[]
  /** Altezza del contenitore mappa (default 320px) */
  height?: number
  className?: string
}

/**
 * Mappa Leaflet che mostra i marker di tutte le sedi di un'azienda.
 * Click sul marker → tooltip con nome sede e indirizzo.
 */
export function SiteMap({ sites, height = 320, className }: SiteMapProps) {
  const validSites = sites.filter((s) => s.latitude && s.longitude)

  if (validSites.length === 0) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface-overlay)] text-sm text-[var(--fc-foreground-muted)] ${className ?? ''}`}
        style={{ height }}
      >
        Nessuna sede con coordinate disponibili
      </div>
    )
  }

  // Centro iniziale: primo marker o Italia
  const center: [number, number] = [validSites[0].latitude, validSites[0].longitude]

  return (
    <div
      className={`overflow-hidden rounded-lg border border-[var(--fc-border)] ${className ?? ''}`}
      style={{ height, isolation: 'isolate', position: 'relative', zIndex: 0 }}
    >
      <MapContainer
        center={center}
        zoom={10}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds markers={validSites} />
        {validSites.map((site) => (
          <Marker key={site.id} position={[site.latitude, site.longitude]}>
            <Popup>
              <div className="min-w-[160px]">
                <p className="font-semibold text-sm text-gray-800">{site.name}</p>
                {site.address && (
                  <p className="text-xs text-gray-500 mt-0.5">{site.address}</p>
                )}
                {site.city && (
                  <p className="text-xs text-gray-500">{site.city}</p>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  )
}
